<template>
  <Modal :open="open" @closed="$emit('closed')" dialog-size="modal-xl" dialog-scrollable disable-backdrop-exit>
    <div class="modal-header bg-light" v-if="form">
      <h5 class="modal-title m-0">
        <i class="bi me-2" :class="meta.icon"></i>{{ meta.label }}
        <small class="text-muted fw-normal ms-2">{{ form.title }}</small>
      </h5>
      <button type="button" class="btn-close" aria-label="Close" @click="$emit('closed')"></button>
    </div>
    <div class="modal-body" v-if="form">
      <ul class="nav nav-tabs mb-3">
        <li class="nav-item" v-for="tab in tabs" :key="tab.key">
          <button type="button" class="nav-link" :class="{ active: activeTab === tab.key }" @click="activeTab = tab.key">
            <i class="bi me-1" :class="tab.icon"></i>{{ tab.label }}
          </button>
        </li>
      </ul>

      <!-- Content -->
      <div v-show="activeTab === 'content'">
        <div class="row g-3">
          <div class="col-md-6">
            <label class="form-label fw-bold">Section title</label>
            <input v-model="form.title" type="text" class="form-control" maxlength="120" :placeholder="titlePlaceholder">
          </div>
          <div class="col-md-6">
            <label class="form-label fw-bold">Subtitle <span class="text-muted small fw-normal">(optional)</span></label>
            <input v-model="form.subtitle" type="text" class="form-control" maxlength="200">
          </div>

          <!-- Hero carousel -->
          <template v-if="section.type === 'hero_carousel'">
            <div class="col-md-4">
              <div class="form-check form-switch mt-md-4">
                <input id="s-autoplay" v-model="form.settings.autoplay" class="form-check-input" type="checkbox">
                <label for="s-autoplay" class="form-check-label">Autoplay slides</label>
              </div>
            </div>
            <div class="col-md-4">
              <label class="form-label fw-bold">Seconds per slide</label>
              <input v-model.number="form.settings.interval" type="number" min="2" max="20" class="form-control">
            </div>
            <div class="col-md-4">
              <div class="form-check form-switch mt-md-4">
                <input id="s-side" v-model="form.settings.show_side_banners" class="form-check-input" type="checkbox">
                <label for="s-side" class="form-check-label">Show 2 side banners next to the carousel</label>
              </div>
            </div>
          </template>

          <!-- Products -->
          <template v-if="isProductSection">
            <div class="col-md-4">
              <label class="form-label fw-bold">Products to show</label>
              <select v-model="form.settings.source" class="form-select">
                <option v-for="(label, key) in productSources" :key="key" :value="key">{{ label }}</option>
              </select>
            </div>
            <div class="col-md-4" v-if="form.settings.source === 'category'">
              <label class="form-label fw-bold">Category</label>
              <select v-model="form.settings.category_id" class="form-select">
                <option :value="null">Select category</option>
                <option v-for="c in categories" :key="c.id" :value="c.id">{{ c.name }}</option>
              </select>
            </div>
            <div class="col-md-4" v-if="form.settings.source === 'brand'">
              <label class="form-label fw-bold">Brand</label>
              <select v-model="form.settings.brand_id" class="form-select">
                <option :value="null">Select brand</option>
                <option v-for="b in brands" :key="b.id" :value="b.id">{{ b.name }}</option>
              </select>
            </div>
            <div class="col-md-4" v-if="form.settings.source === 'tag'">
              <label class="form-label fw-bold">Product tag</label>
              <input v-model="form.settings.tag" type="text" class="form-control" maxlength="60" placeholder="e.g. offer">
            </div>
            <div class="col-md-2">
              <label class="form-label fw-bold">Max products</label>
              <input v-model.number="form.settings.limit" type="number" min="1" max="48" class="form-control">
            </div>
            <div class="col-md-2" v-if="section.type === 'product_grid'">
              <label class="form-label fw-bold">Columns</label>
              <select v-model.number="form.settings.columns" class="form-select">
                <option v-for="n in [2,3,4,5,6]" :key="n" :value="n">{{ n }}</option>
              </select>
            </div>
            <div class="col-md-4">
              <div class="form-check form-switch mt-md-4">
                <input id="s-viewall" v-model="form.settings.show_view_all" class="form-check-input" type="checkbox">
                <label for="s-viewall" class="form-check-label">Show "View all" link</label>
              </div>
            </div>
            <div class="col-12" v-if="form.settings.source === 'manual'">
              <label class="form-label fw-bold">Hand-picked products</label>
              <ProductPicker v-model="form.settings.product_ids" :initial-products="section.selected_products || []" />
            </div>
          </template>

          <!-- Categories -->
          <template v-if="section.type === 'category_grid'">
            <div class="col-md-4">
              <label class="form-label fw-bold">Categories</label>
              <select v-model="form.settings.source" class="form-select">
                <option value="all">All categories with products</option>
                <option value="manual">Choose categories</option>
              </select>
            </div>
            <div class="col-md-4">
              <label class="form-label fw-bold">Style</label>
              <select v-model="form.settings.style" class="form-select">
                <option value="circle">Circles</option>
                <option value="tile">Tiles</option>
                <option value="card">Cards</option>
              </select>
            </div>
            <div class="col-md-4">
              <label class="form-label fw-bold">Max categories</label>
              <input v-model.number="form.settings.limit" type="number" min="1" max="48" class="form-control">
            </div>
            <div class="col-12" v-if="form.settings.source === 'manual'">
              <OrderedMultiSelect v-model="form.settings.category_ids" :options="categories" placeholder="Add a category..." />
            </div>
          </template>

          <!-- Brands -->
          <template v-if="section.type === 'brand_strip'">
            <div class="col-md-4">
              <label class="form-label fw-bold">Brands</label>
              <select v-model="form.settings.source" class="form-select">
                <option value="all">All brands with products (featured first)</option>
                <option value="manual">Choose brands</option>
              </select>
            </div>
            <div class="col-md-4">
              <label class="form-label fw-bold">Style</label>
              <select v-model="form.settings.style" class="form-select">
                <option value="logo">Logos</option>
                <option value="card">Cards with name</option>
                <option value="circle">Circles</option>
              </select>
            </div>
            <div class="col-md-4">
              <label class="form-label fw-bold">Max brands</label>
              <input v-model.number="form.settings.limit" type="number" min="1" max="48" class="form-control">
            </div>
            <div class="col-12" v-if="form.settings.source === 'manual'">
              <OrderedMultiSelect v-model="form.settings.brand_ids" :options="brands" placeholder="Add a brand..." />
            </div>
          </template>

          <!-- Banners -->
          <div class="col-md-4" v-if="section.type === 'banner'">
            <div class="form-check form-switch">
              <input id="s-full" v-model="form.settings.full_width" class="form-check-input" type="checkbox">
              <label for="s-full" class="form-check-label">Stretch to full screen width</label>
            </div>
          </div>
          <div class="col-md-4" v-if="section.type === 'banner_grid'">
            <label class="form-label fw-bold">Columns</label>
            <select v-model.number="form.settings.columns" class="form-select">
              <option v-for="n in [2,3,4]" :key="n" :value="n">{{ n }}</option>
            </select>
          </div>

          <!-- Text -->
          <template v-if="section.type === 'text_block'">
            <div class="col-12">
              <label class="form-label fw-bold">Text</label>
              <textarea v-model="form.settings.content" class="form-control" rows="5" maxlength="2000"></textarea>
            </div>
            <div class="col-md-4">
              <label class="form-label fw-bold">Alignment</label>
              <select v-model="form.settings.alignment" class="form-select">
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
              </select>
            </div>
            <div class="col-md-4">
              <label class="form-label fw-bold">Text color</label>
              <ColorInput v-model="form.settings.text_color" allow-empty />
            </div>
          </template>

          <div class="col-md-4" v-if="section.type === 'newsletter'">
            <label class="form-label fw-bold">Button text</label>
            <input v-model="form.settings.button_text" type="text" class="form-control" maxlength="40">
          </div>

          <div class="col-md-4" v-if="hasBackground">
            <label class="form-label fw-bold">Background color</label>
            <ColorInput v-model="form.settings.background_color" allow-empty />
          </div>
        </div>
      </div>

      <!-- Items -->
      <div v-if="activeTab === 'items'">
        <SectionItems :section="section" :categories="categories" :brands="brands" @changed="$emit('items-changed')" />
      </div>

      <!-- Visibility -->
      <div v-show="activeTab === 'visibility'">
        <div class="row g-3">
          <div class="col-12">
            <div class="form-check form-switch">
              <input id="s-active" v-model="form.is_active" class="form-check-input" type="checkbox">
              <label for="s-active" class="form-check-label">Section is visible in the shop</label>
            </div>
          </div>
          <div class="col-md-4">
            <label class="form-label fw-bold">Show in store</label>
            <select v-model="form.store_id" class="form-select">
              <option :value="null">All online stores</option>
              <option v-for="store in stores" :key="store.id" :value="store.id">{{ store.online_name || store.name }}</option>
            </select>
          </div>
          <div class="col-md-4">
            <label class="form-label fw-bold">Start showing on</label>
            <input v-model="form.starts_at" type="datetime-local" class="form-control">
          </div>
          <div class="col-md-4">
            <label class="form-label fw-bold">Stop showing on</label>
            <input v-model="form.ends_at" type="datetime-local" class="form-control">
          </div>
          <div class="col-12">
            <small class="text-muted">
              <i class="bi bi-info-circle me-1"></i>Leave dates empty to always show the section. Use them to schedule
              seasonal promotions (e.g. Black Friday banners).
            </small>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-footer bg-light" v-if="form">
      <button type="button" class="btn btn-outline-secondary" @click="$emit('closed')">Close</button>
      <button v-if="activeTab !== 'items'" type="button" class="btn btn-primary" :disabled="saving" @click="save">
        <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>Save section
      </button>
    </div>
  </Modal>
</template>

<script>
import storefrontService from "@/api/storefrontService";
import ProductPicker from "./ProductPicker.vue";
import OrderedMultiSelect from "./OrderedMultiSelect.vue";
import SectionItems from "./SectionItems.vue";
import ColorInput from "./ColorInput.vue";
import { PRODUCT_SOURCES, SECTION_TYPES } from "../storefrontMeta";

const toLocalInput = (value) => {
  if (!value) return ''
  const date = new Date(value)
  if (isNaN(date)) return ''
  const pad = n => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

// Send wall-clock time; the API interprets it in the application's timezone
const toApiDate = (value) => value ? value.replace('T', ' ') + ':00' : null

export default {
  name: "SectionEditor",

  components: { ProductPicker, OrderedMultiSelect, SectionItems, ColorInput },

  emits: ['closed', 'saved', 'items-changed'],

  props: {
    open: { type: Boolean, default: false },
    section: { type: Object, default: null },
    categories: { type: Array, default: () => [] },
    brands: { type: Array, default: () => [] },
    stores: { type: Array, default: () => [] },
    sectionDefaults: { type: Object, default: () => ({}) },
    initialTab: { type: String, default: 'content' },
  },

  data() {
    return {
      form: null,
      saving: false,
      activeTab: 'content',
      productSources: PRODUCT_SOURCES,
    }
  },

  computed: {
    meta() {
      return SECTION_TYPES[this.section?.type] || { label: 'Section', icon: 'bi-square' }
    },
    isProductSection() {
      return ['product_carousel', 'product_grid'].includes(this.section?.type)
    },
    hasBackground() {
      return ['product_carousel', 'product_grid', 'features', 'text_block', 'newsletter'].includes(this.section?.type)
    },
    titlePlaceholder() {
      return this.section?.type === 'hero_carousel' ? 'Not shown for the hero carousel' : 'e.g. Fresh Deals'
    },
    tabs() {
      const tabs = [{ key: 'content', label: 'Content & settings', icon: 'bi-sliders' }]
      if (this.meta.hasItems) tabs.push({ key: 'items', label: 'Images & items', icon: 'bi-images' })
      tabs.push({ key: 'visibility', label: 'Visibility & schedule', icon: 'bi-calendar-event' })
      return tabs
    },
  },

  watch: {
    open(value) {
      if (value) this.reset()
    },
  },

  methods: {
    reset() {
      if (!this.section) return
      const defaults = this.sectionDefaults[this.section.type] || {}
      this.form = {
        title: this.section.title || '',
        subtitle: this.section.subtitle || '',
        is_active: !!this.section.is_active,
        store_id: this.section.store_id || null,
        starts_at: toLocalInput(this.section.starts_at),
        ends_at: toLocalInput(this.section.ends_at),
        settings: JSON.parse(JSON.stringify({ ...defaults, ...(this.section.settings || {}) })),
      }
      this.activeTab = this.tabs.some(t => t.key === this.initialTab) ? this.initialTab : 'content'
    },
    async save() {
      this.saving = true
      try {
        const payload = {
          title: this.form.title || null,
          subtitle: this.form.subtitle || null,
          is_active: this.form.is_active,
          store_id: this.form.store_id || null,
          starts_at: toApiDate(this.form.starts_at),
          ends_at: toApiDate(this.form.ends_at),
          settings: this.cleanSettings(this.form.settings),
        }
        const result = await storefrontService.updateSection(this.section.id, payload)
        if (result) this.$emit('saved', result)
      } finally {
        this.saving = false
      }
    },
    cleanSettings(settings) {
      const clean = { ...settings }
      if (clean.source !== 'category') clean.category_id = null
      if (clean.source !== 'brand') clean.brand_id = null
      Object.keys(clean).forEach(key => {
        if (clean[key] === '' && ['background_color', 'text_color'].includes(key)) clean[key] = null
      })
      if (!clean.tag) delete clean.tag
      return clean
    },
  },
}
</script>
